import { useState, useRef } from "react";
import {
  Star,
  GitFork,
  Users,
  UserCheck,
  BookOpen,
  MapPin,
  Link2,
  Building2,
  Twitter,
  ExternalLink,
  Terminal,
  CircleDot,
  Clock,
  Code2,
} from "lucide-react";
import styles from "../style";
import { timeAgo, fmt } from "../utils/helper";
import LANG_COLORS from "../utils/languageColors";

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function GitHubFinder() {
  const [username, setUsername] = useState("");
  // user holds the profile object from GitHub's /users/:username endpoint
  const [user, setUser] = useState(null);
  // repos holds an array from the /users/:username/repos endpoint
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // logs is an array of status lines shown while/after fetching — gives terminal feel
  const [logs, setLogs] = useState([]);
  const inputRef = useRef(null);

  // Adds a line to our terminal log display
  // type is "ok" | "err" | "warn" — controls the color of the tag
  function addLog(tag, msg, type = "ok") {
    setLogs((prev) => [...prev, { tag, msg, type }]);
  }

  async function fetchProfile() {
    if (!username.trim()) return;
    setLoading(true);
    setError("");
    setUser(null);
    setRepos([]);
    setLogs([]); // clear old logs

    try {
      addLog("[INIT]", `Connecting to api.github.com...`);
      addLog("[QUERY]", `GET /users/${username.trim()}`);

      // GitHub's public API — no key needed for basic profile lookups
      const userRes = await fetch(
        `https://api.github.com/users/${username.trim()}`,
      );

      // 404 means user doesn't exist
      if (userRes.status === 404)
        throw new Error(`User "${username}" not found on GitHub.`);
      // 403 means we hit the rate limit (60 req/hr without a key)
      if (userRes.status === 403)
        throw new Error("Rate limit hit. Wait a minute and try again.");
      if (!userRes.ok)
        throw new Error(`GitHub returned status ${userRes.status}`);

      const userData = await userRes.json();
      addLog("[200 OK]", `User found: ${userData.name || userData.login}`);

      // Fetch repos sorted by most recently pushed, limit 6
      addLog(
        "[QUERY]",
        `GET /users/${username.trim()}/repos?sort=pushed&per_page=6`,
      );
      const reposRes = await fetch(
        `https://api.github.com/users/${username.trim()}/repos?sort=pushed&per_page=6`,
      );
      const reposData = await reposRes.json();
      addLog("[200 OK]", `Loaded ${reposData.length} repositories`);
      addLog("[DONE]", `Render complete ✓`);

      setUser(userData);
      setRepos(reposData);
    } catch (err) {
      addLog("[ERROR]", err.message, "err");
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleKey(e) {
    if (e.key === "Enter") fetchProfile();
  }

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{styles}</style>
      <div className="app">
        {/* ── Header ── */}
        <div className="header">
          <div className="header-top">
            <Terminal size={18} />
            <h1>
              GITHUB_FINDER
              <span className="blink" />
            </h1>
          </div>
          <div className="header-sub">
            query any github user profile + repositories — no auth required
          </div>
        </div>

        {/* ── Search ── */}
        <div className="search-area">
          <div className="prompt-line">
            <span className="prompt-sym">$</span>
            <span>Enter GitHub username to query</span>
          </div>
          <div className="search-row">
            {/* The $ prefix inside the input box — pure visual, not part of the value */}
            <span className="input-prefix">~/search »</span>
            <input
              ref={inputRef}
              type="text"
              placeholder="e.g. torvalds, gaearon, addyosmani"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={handleKey}
              autoFocus
            />
            <button onClick={fetchProfile} disabled={loading}>
              <CircleDot size={13} />
              {loading ? "QUERYING..." : "EXECUTE"}
            </button>
          </div>
        </div>

        {/* ── Terminal log lines ── */}
        {logs.length > 0 && (
          <div className="log">
            {logs.map((l, i) => (
              <div className={`log-line ${l.type}`} key={i}>
                <span className="tag">{l.tag}</span>
                <span className="msg">{l.msg}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Loading typewriter lines ── */}
        {loading && (
          <div className="loading-lines">
            <div className="type-line">Resolving DNS for api.github.com...</div>
            <div className="type-line">Sending GET request...</div>
            <div className="type-line">Awaiting response...</div>
          </div>
        )}

        {/* ── Error ── */}
        {error && (
          <div className="error-box">
            <span>[ERR]</span> {error}
          </div>
        )}

        {/* ── Profile Card ── */}
        {user && !loading && (
          <div className="profile-card">
            {/* Fake terminal title bar */}
            <div className="card-titlebar">
              <span className="dot active" />
              <span className="dot" />
              <span className="dot" />
              <span>github.com/{user.login} — profile.json</span>
            </div>

            {/* Avatar + name + bio + meta */}
            <div className="profile-top">
              <div className="avatar-wrap">
                {/* GitHub's avatar URL comes straight from the API response */}
                <img src={user.avatar_url} alt={user.login} />
              </div>

              <div>
                <div className="profile-name">{user.name || user.login}</div>
                <div className="profile-login">
                  login: <span>@{user.login}</span>
                </div>
                {user.bio && <div className="profile-bio">{user.bio}</div>}

                {/* Only render meta items if the data exists (user might not have all fields) */}
                <div className="profile-meta">
                  {user.location && (
                    <span className="meta-item">
                      <MapPin size={11} /> {user.location}
                    </span>
                  )}
                  {user.company && (
                    <span className="meta-item">
                      <Building2 size={11} /> {user.company}
                    </span>
                  )}
                  {user.blog && (
                    <span className="meta-item">
                      <Link2 size={11} />
                      <a href={user.blog} target="_blank" rel="noreferrer">
                        {user.blog.replace(/^https?:\/\//, "")}
                      </a>
                    </span>
                  )}
                  {user.twitter_username && (
                    <span className="meta-item">
                      <Twitter size={11} /> @{user.twitter_username}
                    </span>
                  )}
                  <span className="meta-item">
                    <Clock size={11} /> Joined {timeAgo(user.created_at)}
                  </span>
                </div>
              </div>
            </div>

            {/* ── Stats row ── */}
            <div className="stat-row">
              {[
                {
                  icon: <BookOpen size={13} />,
                  label: "Repos",
                  val: fmt(user.public_repos),
                },
                {
                  icon: <Users size={13} />,
                  label: "Followers",
                  val: fmt(user.followers),
                },
                {
                  icon: <UserCheck size={13} />,
                  label: "Following",
                  val: fmt(user.following),
                },
                {
                  icon: <Code2 size={13} />,
                  label: "Gists",
                  val: fmt(user.public_gists),
                },
              ].map((s) => (
                <div className="stat-cell" key={s.label}>
                  <div className="stat-label-row">
                    {s.icon} {s.label}
                  </div>
                  <div className="stat-num">{s.val}</div>
                </div>
              ))}
            </div>

            {/* ── Recent Repos ── */}
            {repos.length > 0 && (
              <div className="repos-section">
                <div className="repos-header">Recent Repositories</div>
                <div className="repos-grid">
                  {repos.map((repo) => (
                    // Each card is an <a> tag pointing to the repo on GitHub
                    <a
                      className="repo-card"
                      key={repo.id}
                      href={repo.html_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <div className="repo-name">
                        <BookOpen size={13} />
                        {repo.name}
                        <ExternalLink
                          size={11}
                          style={{ marginLeft: "auto", opacity: 0.4 }}
                        />
                      </div>

                      {/* Description can be null — only render if it exists */}
                      {repo.description && (
                        <div className="repo-desc">{repo.description}</div>
                      )}

                      <div className="repo-footer">
                        {/* Stars */}
                        <span>
                          <Star size={11} /> {repo.stargazers_count}
                        </span>
                        {/* Forks */}
                        <span>
                          <GitFork size={11} /> {repo.forks_count}
                        </span>
                        {/* Language — show colored dot if we have a color for it */}
                        {repo.language && (
                          <span>
                            <span
                              className="lang-dot"
                              style={{
                                background:
                                  LANG_COLORS[repo.language] || "#00ff41",
                              }}
                            />
                            {repo.language}
                          </span>
                        )}
                        {/* Last push */}
                        <span style={{ marginLeft: "auto" }}>
                          {timeAgo(repo.pushed_at)}
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Idle state — before any search ── */}
        {!user && !loading && !error && (
          <div className="idle">
            <Terminal size={40} />
            <p>
              Type any GitHub username above and press <code>EXECUTE</code> or{" "}
              <code>Enter</code>
              <br />
              to fetch their public profile and repositories.
              <br />
              <br />
              Try: <code>torvalds</code> · <code>gaearon</code> ·{" "}
              <code>sindresorhus</code>
            </p>
          </div>
        )}
      </div>
    </>
  );
}
